import { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import ColorPicker from '@components/ColorPicker/ColorPicker';
import { useConfiguratorStore } from '@store/useConfiguratorStore';
import './BottomPanel.css';

export default function BottomPanel() {
    const { modelParts, selectedPart, setSelectedPart, printAreas, currentModel } = useConfiguratorStore();
    const [localSelected, setLocalSelected] = useState('__ALL__');

    const itemsRef = useRef<Map<string, HTMLButtonElement>>(new Map());

    const scrollToPart = (partId: string) => {
        const node = itemsRef.current.get(partId);
        if (node) {
            node.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
    };

    // Sync dropdown when part is clicked in 3D
    useEffect(() => {
        if (selectedPart) {
            setLocalSelected(selectedPart);
            scrollToPart(selectedPart);
        } else {
            setLocalSelected('__ALL__');
            scrollToPart('__ALL__');
        }
    }, [selectedPart]);

    // Build parts list from actual model
    const parts = [
        { id: '__ALL__', name: 'All Parts' },
        // Standard Parts
        ...modelParts.map(part => ({
            id: part.name,
            name: part.name.charAt(0).toUpperCase() + part.name.slice(1).replace(/_/g, ' ')
        })),
        // Print Areas (as colorable parts) - FXC-123 exclusion for Square Pen
        ...(currentModel !== 'square-pen' ? printAreas
            .filter(pa => pa.id !== 'all-faces')
            .map(pa => ({
                id: pa.id,
                name: `PrintArea_${pa.name}` // Requested format: PrintArea_[part name]
            })) : [])
    ];

    // Update part color when a color is selected
    const handlePartSelect = (partId: string) => {
        setLocalSelected(partId);
        setSelectedPart(partId === '__ALL__' ? null : partId);
        // Also scroll when manually selected (optional but good for consistency)
        scrollToPart(partId);
    };

    const partsContainerRef = useRef<HTMLDivElement>(null);

    const handleScrollLeft = () => {
        if (partsContainerRef.current) {
            partsContainerRef.current.scrollBy({ left: -100, behavior: 'smooth' });
        }
    };

    const handleScrollRight = () => {
        if (partsContainerRef.current) {
            partsContainerRef.current.scrollBy({ left: 100, behavior: 'smooth' });
        }
    };

    return (
        <div className="bottom-panel">
            <div className="bottom-panel-container">
                {/* Colors Section (First/Left as per design) */}
                <div className="bottom-panel-section colors-section">
                    <p className="section-label">Colors</p>
                    <div className="selector-pill color-picker-wrapper">
                        <ColorPicker selectedPart={localSelected} />
                    </div>
                </div>

                {/* Part Selector (Second/Right replacing Material) */}
                <div className="bottom-panel-section parts-section">
                    <p className="section-label">Parts</p>
                    <div className="selector-pill">
                        <button 
                            className="selector-nav-btn" 
                            onClick={handleScrollLeft}
                            title="Previous"
                        >
                            <ChevronLeft size={18} />
                        </button>
                        


                        <div className="selector-scroll-container" ref={partsContainerRef}>
                            <button
                                ref={(node) => {
                                    if (node) itemsRef.current.set('__ALL__', node);
                                    else itemsRef.current.delete('__ALL__');
                                }}
                                className={`part-chip ${localSelected === '__ALL__' ? 'active' : ''}`}
                                onClick={() => handlePartSelect('__ALL__')}
                            >
                                All Parts
                            </button>
                            {parts.slice(1).map(part => (
                                <button
                                    key={part.id}
                                    ref={(node) => {
                                        if (node) itemsRef.current.set(part.id, node);
                                        else itemsRef.current.delete(part.id);
                                    }}
                                    className={`part-chip ${localSelected === part.id ? 'active' : ''}`}
                                    onClick={() => handlePartSelect(part.id)}
                                >
                                    {part.name}
                                </button>
                            ))}
                        </div>

                        <button 
                            className="selector-nav-btn"
                            onClick={handleScrollRight}
                            title="Next"
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
